<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import {
  ArrowNarrowRight,
  ChevronDown,
  Copy06,
  InfoCircle,
  LinkExternal02,
  Pencil02,
  SearchMd,
  Send03,
  X,
  Check,
} from "utruckin-icons";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import CommonStatus from "@/components/common/CommonStatus.vue";
import { useTaskStore } from "@/features/tasks/task.store";
import { useTeamStore } from "@/features/teams/team.store";
import { useWorkTypeStore } from "@/features/work-types/work-type.store";
import { INewTask, ITaskData } from "@/features/tasks/task.types";
import { ITeam } from "@/features/teams/team.types";
import { IWorkType } from "@/features/work-types/work-type.types";
import { TaskStatus } from "@/features/tasks/task.enum";
import { DateTime } from "luxon";
import CustomTableDropdown from "@/components/custom/CustomTableDropdown.vue";
import TaskAdditionalWorkTypes from "@/components/custom/TaskAdditionalWorkTypes.vue";
import { taskService } from "@/features/tasks/task.service";
import { useCompanyStore } from "@/features/company/company.store";
import { ICompany } from "@/features/company/company.types";
import { useUserStore } from "@/features/users/user.store";
import { useDriverStore } from "@/features/drivers/drivers.store";
import { IDriver } from "@/features/drivers/drivers.types";
import { useNotification } from "@/utils/notification";
import { useAppStore } from "@/store/app.store";
import { IDropdown } from "@/types/main";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import { useArchiveStore } from "@/features/archive/archive.store";
import CommonInput from "@/components/common/CommonInput.vue";
import { IUser } from "@/features/users/user.types";
import CustomStatusBadge from "@/components/custom/CustomStatusBadge.vue";
import CustomSearchDropdown from "@/components/custom/CustomSearchDropdown.vue";
import { onBeforeMount } from "vue";
import { useCheckStore } from "@/features/check/check.store";
import router from "@/router";
import { companyService } from "@/features/company/company.service";
import { driverService } from "@/features/drivers/drivers.service";
import { workTypeService } from "@/features/work-types/work-type.service";

const taskStore = useTaskStore();
const teamStore = useTeamStore();
const workTypeStore = useWorkTypeStore();
const companyStore = useCompanyStore();
const usersStore = useUserStore();
const driverStore = useDriverStore();
const appStore = useAppStore();
const archiveStore = useArchiveStore();
const checkStore = useCheckStore();
const { pushNotification } = useNotification();

const tasks = ref<ITaskData[]>([]);
const teams = ref<ITeam[]>([]);
const users = ref<IUser[]>([]);
const companies = ref<ICompany[]>([]);
const workTypes = ref<IWorkType[]>([]);
const drivers = ref<IDriver[]>([]);
const problems = ref<IWorkType[]>([]);

const tasksLoading = ref(false);
const teamLoading = ref(false);
const userLoading = ref(false);
const workTypeLoading = ref(false);
const companyLoading = ref(false);
const driverLoading = ref(false);

const team = ref<string>("");
const user = ref<string>("");
const status = ref<string>("");
const problem = ref<string>("");
const search = ref<string>("");
const isAdmin = ref<boolean>(false);
const newTaskStatus = ref<string>("new");
const description = ref<string>("");
const newModal = ref(false);

const taskModal = ref<boolean>(false);
const taskModalItem = ref<any>();
const taskModalLoading = ref<boolean>(false);
const taskModalComment = ref<string>("");

const archiveModal = ref(false);
const archiveReason = ref<string>("");
const archiveModalItem = ref<ITaskData | null>(null);
const isSaving = ref<boolean>(false);

let searchTimer: ReturnType<typeof setTimeout> | null = null;
let searchCompanyTimer: ReturnType<typeof setTimeout> | null = null;
let searchDriverTimer: ReturnType<typeof setTimeout> | null = null;
let searchProblemTimer: ReturnType<typeof setTimeout> | null = null;
let searchFilterProblemTimer: ReturnType<typeof setTimeout> | null = null;
let searchFilterUserTimer: ReturnType<typeof setTimeout> | null = null;

const collapsedDates = ref<Record<string, boolean>>({});
const collapsedShifts = ref<Record<string, boolean>>({});

const dropdown1 = ref();
const dropdown2 = ref();
const dropdown3 = ref();

const PAGE_SIZE = 50;
const page = ref(1);
const hasMore = ref(true);
const isLoadingMore = ref(false);
const scrollContainer = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.NEW]: "--color-system-blue",
  [TaskStatus.INFORM]: "--color-system-grey",
  [TaskStatus.IN_PROGRESS]: "--color-system-orange",
  [TaskStatus.ON_HOLD]: "--color-system-yellow",
  [TaskStatus.DONE]: "--color-system-green",
  [TaskStatus.QA_FAILED]: "--color-system-red",
  [TaskStatus.CLOSED]: "--color-system-green",
};

const newTask = ref<INewTask>({
  company_id: "",
  driver_id: "",
  work_type_id: "",
  priority: "",
  assignee_id: "",
});

const companyItems = computed(() =>
  (companies.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: c.provider_name,
  }))
);

const driverItems = computed(() =>
  (drivers.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: c.truck_number,
  }))
);

const problemItems = computed(() =>
  (problems.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: String(c.base_points),
  }))
);

const problemFilterItems = computed(() =>
  (workTypes.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
  }))
);

const userFilterItems = computed(() =>
  (users.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
  }))
);

const filteredItems = computed(() => {
  if (taskStore.commonFilter.team_id) {
    return [
      {
        name: "id",
        label: "No",
        width: "3%",
      },
      {
        name: "company",
        label: "Company",
        width: "14%",
      },
      {
        name: "drivers",
        label: "Drivers",
        width: "14%",
      },
      {
        name: "users",
        label: "User",
        width: "13%",
      },
      {
        name: "date",
        label: "Start time",
        width: "6%",
      },
      {
        name: "completed_time",
        label: "End time",
        width: "6%",
      },
      {
        name: "points_awarded",
        label: "KPI",
        width: "3%",
      },
      {
        name: "status",
        label: "Status",
        width: "9%",
      },
      {
        name: "problems",
        label: "Problems",
        width: "29%",
      },
      {
        name: "actions",
        label: "",
        width: "3%",
      },
    ];
  } else {
    return [
      {
        name: "id",
        label: "No",
        width: "3%",
      },
      {
        name: "company",
        label: "Company",
        width: "14%",
      },
      {
        name: "drivers",
        label: "Drivers",
        width: "14%",
      },
      {
        name: "team",
        label: "Team",
        width: "5%",
      },
      {
        name: "users",
        label: "User",
        width: "13%",
      },
      {
        name: "date",
        label: "Start time",
        width: "6%",
      },
      {
        name: "completed_time",
        label: "End time",
        width: "6%",
      },
      {
        name: "points_awarded",
        label: "KPI",
        width: "3%",
      },
      {
        name: "status",
        label: "Status",
        width: "9%",
      },
      {
        name: "problems",
        label: "Problems",
        width: "24%",
      },
      {
        name: "actions",
        label: "",
        width: "3%",
      },
    ];
  }
});

const hasUserHasAction = (item: ITaskData) => {
  return (
    usersStore.userProfile &&
    (usersStore.userProfile.id == item.assignee_id ||
      usersStore.userProfile.role == "admin" ||
      usersStore.userProfile.role == "super_admin" ||
      usersStore.userProfile.role == "supervisor")
  );
};

const formatStatusLabel = (key: string) =>
  key.replace(/_/g, "_").replace(/\b\w/g, (c) => c.toUpperCase());

const statusItems = (
  Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>
).map((key) => ({
  value: TaskStatus[key],
  name: formatStatusLabel(key),
  color: STATUS_COLORS[TaskStatus[key]],
}));

const getStatus = (qaStatus: string) => {
  const key = (Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>).find(
    (k) => TaskStatus[k] === qaStatus
  );
  return key ? formatStatusLabel(key) : null;
};

const toggleDate = (date: string) => {
  collapsedDates.value[date] = !collapsedDates.value[date];
};

const toggleShift = (date: string, shift: string) => {
  const key = `${date}::${shift}`;
  collapsedShifts.value[key] = !collapsedShifts.value[key];
};

const isDateCollapsed = (date: string) => collapsedDates.value[date];
const isShiftCollapsed = (date: string, shift: string) =>
  collapsedShifts.value[`${date}::${shift}`];

const groupedByDate = computed(() => {
  const dateMap = new Map<string, Map<string, ITaskData[]>>();

  for (const task of tasks.value) {
    const date = task.created_time
      ? DateTime.fromISO(task.created_time, { zone: "utc" })
          .setZone("America/New_York")
          .toFormat("yyyy-MM-dd")
      : "Unknown";

    const shift = task.shift_name ?? "Unknown Shift";

    if (!dateMap.has(date)) {
      dateMap.set(date, new Map());
    }

    const shiftMap = dateMap.get(date)!;

    if (!shiftMap.has(shift)) {
      shiftMap.set(shift, []);
    }

    shiftMap.get(shift)!.push(task);
  }

  return Array.from(dateMap.entries()).map(([date, shiftMap]) => {
    const shiftGroups = Array.from(shiftMap.entries()).map(
      ([label, items]) => ({
        label,
        items,
      })
    );

    return { date, shiftGroups };
  });
});

function datepickerToUtcDateOnly(d: string | Date | null | undefined) {
  if (!d) return "";

  const date =
    typeof d === "string"
      ? DateTime.fromISO(d, { zone: "utc" })
      : DateTime.fromJSDate(d, { zone: "utc" });

  return date.setZone("America/New_York").toFormat("HH:mm:ss");
}

function datepickerToUtcDateOnlyWithoutTime(d: any) {
  return d
    ? DateTime.fromISO(d, {
        zone: "utc",
      })
        .setZone("America/New_York")
        .toFormat("MMM d, HH:mm")
    : "-";
}

let filterVersion = 0;

const fetchTasks = async (
  requestedPage: number,
  append: boolean,
  version: number
): Promise<boolean> => {
  const loadingRef = append ? isLoadingMore : tasksLoading;

  loadingRef.value = true;

  // Snapshot: each request keeps its own filter values.
  const filters = {
    ...taskStore.commonFilter,
    page: requestedPage,
    per_page: PAGE_SIZE,
  };

  try {
    const { data } = await taskService.getTasks(filters);

    // Ignore responses from an outdated filter version.
    if (version !== filterVersion) return false;

    const items: ITaskData[] = data.items ?? [];

    tasks.value = append ? [...tasks.value, ...items] : items;

    page.value = requestedPage;
    taskStore.commonFilter.page = requestedPage;

    hasMore.value =
      data.has_more !== undefined ? data.has_more : items.length === PAGE_SIZE;

    return true;
  } catch (err: any) {
    if (version === filterVersion) {
      pushNotification(
        err.response?.data?.detail || "Failed to load tasks",
        "error"
      );
    }

    return false;
  } finally {
    if (!append && version === filterVersion) {
      tasksLoading.value = false;
    }

    if (append) {
      isLoadingMore.value = false;
    }
  }
};

const resetAndFetch = async () => {
  const version = ++filterVersion;

  tasks.value = [];

  page.value = 1;
  hasMore.value = true;

  // Block infinite scroll while the first page loads.
  tasksLoading.value = true;

  await fetchTasks(1, false, version);
};

const loadMore = async () => {
  if (
    tasksLoading.value ||
    isLoadingMore.value ||
    searchTimer !== null ||
    !hasMore.value
  ) {
    return;
  }

  const version = filterVersion;
  const nextPage = page.value + 1;

  await fetchTasks(nextPage, true, version);
};

let companySearchVersion = 0;
let driverSearchVersion = 0;
let problemSearchVersion = 0;
let filterWorkTypeVersion = 0;

const fetchCompanyOptions = async (searchText = "") => {
  const version = ++companySearchVersion;

  try {
    const { data } = await companyService.getSimpleCompanies({
      ...companyStore.commonSimpleFilter,
      search: searchText.trim(),
      page: 1,
    });

    if (version !== companySearchVersion) return;

    companies.value = data.items ?? [];
  } catch (error) {
    if (version === companySearchVersion) {
      console.error("Failed to load companies", error);
    }
  }
};

const fetchDriverOptions = async (
  searchText = "",
  companyId = newTask.value.company_id
) => {
  const version = ++driverSearchVersion;

  if (!companyId || newTask.value.company_name) {
    drivers.value = [];
    return;
  }

  try {
    const { data } = await driverService.getSimpleDrivers({
      ...driverStore.commonSimpleFilter,
      search: searchText.trim(),
      company_id: companyId,
      page: 1,
    });

    if (
      version !== driverSearchVersion ||
      newTask.value.company_id !== companyId
    ) {
      return;
    }

    drivers.value = data.items ?? [];
  } catch (error) {
    if (version === driverSearchVersion) {
      console.error("Failed to load drivers", error);
    }
  }
};

const fetchProblemOptions = async (searchText = "") => {
  const version = ++problemSearchVersion;

  try {
    const { data } = await workTypeService.getWorkTypes({
      is_active: true,
      search: searchText.trim(),
    });

    if (version !== problemSearchVersion) return;

    problems.value = data;
  } catch (error) {
    if (version === problemSearchVersion) {
      console.error("Failed to load work types", error);
    }
  }
};

const getList = async () => {
  if (searchCompanyTimer) clearTimeout(searchCompanyTimer);
  if (searchDriverTimer) clearTimeout(searchDriverTimer);
  if (searchProblemTimer) clearTimeout(searchProblemTimer);
  if (searchFilterProblemTimer) clearTimeout(searchFilterProblemTimer);
  if (searchFilterUserTimer) clearTimeout(searchFilterUserTimer);

  searchCompanyTimer = null;
  searchDriverTimer = null;
  searchProblemTimer = null;
  searchFilterProblemTimer = null;

  companyStore.commonSimpleFilter.search = "";
  driverStore.commonSimpleFilter.search = "";
  workTypeStore.commonFilter.search = "";

  // Invalidate previous searches before reloading reference lists.
  companySearchVersion++;
  driverSearchVersion++;
  problemSearchVersion++;
  filterWorkTypeVersion++;

  const companyVersion = companySearchVersion;
  const problemVersion = problemSearchVersion;
  const filterVersionSnapshot = filterWorkTypeVersion;

  // Invalidate pending driver requests for previous companies.
  drivers.value = [];

  const [fetchedTeams, workTypesResponse, companiesResponse, fetchUsers] =
    await Promise.all([
      teamStore.getTeams(teamLoading),
      workTypeService.getWorkTypes({
        is_active: true,
        search: "",
      }),
      companyService.getSimpleCompanies({
        ...companyStore.commonSimpleFilter,
        search: "",
        page: 1,
      }),
      usersStore.getUsers(userLoading),
    ]);

  const fetchedWorkTypes = workTypesResponse.data as IWorkType[];
  const fetchedCompanies = companiesResponse.data;

  await resetAndFetch();

  teams.value = fetchedTeams;
  if (filterVersionSnapshot === filterWorkTypeVersion) {
    workTypes.value = fetchedWorkTypes;
  }

  if (companyVersion === companySearchVersion) {
    companies.value = fetchedCompanies.items;
  }

  if (problemVersion === problemSearchVersion) {
    problems.value = fetchedWorkTypes;
  }
  users.value = fetchUsers;

  newTask.value = {
    company_id: "",
    driver_id: "",
    work_type_id: "",
    priority: "",
    assignee_id: "",
  };
};

const nextStatus = async (item: ITaskData) => {
  if (item.status == "new") {
    const data = await taskStore.startTask(tasksLoading, item.id);
    if (data) {
      pushNotification("Successfully changed", "success");
      await getList();
    }
  } else if (item.status == "in_progress") {
    const data = await taskStore.doneTask(tasksLoading, item.id);
    if (data) {
      pushNotification("Successfully changed", "success");
      await getList();
    }
  }
};

const confirmStatus = async (item: ITaskData) => {
  if (item.status == "in_progress") {
    const data = await taskStore.doneTask(tasksLoading, item.id);
    if (data) {
      pushNotification("Successfully changed", "success");
      await getList();
    }
  }
};

const availableStatusItems = (item: ITaskData) => {
  let allStatus: any[] = [];

  if (
    usersStore.userProfile?.role == "admin" ||
    usersStore.userProfile?.role == "super_admin"
  ) {
    allStatus = (
      Object.keys({
        IN_PROGRESS: "in_progress",
        ON_HOLD: "on_hold",
        DONE: "done",
        CLOSED: "closed",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  } else {
    if (item.status == "new") {
      allStatus = (
        Object.keys({
          IN_PROGRESS: "in_progress",
          ON_HOLD: "on_hold",
          DONE: "done",
        }) as Array<keyof typeof TaskStatus>
      ).map((key) => ({
        value: TaskStatus[key],
        name: formatStatusLabel(key),
        color: STATUS_COLORS[TaskStatus[key]],
      }));
    } else if (item.status == "in_progress") {
      allStatus = (
        Object.keys({
          ON_HOLD: "on_hold",
          DONE: "done",
        }) as Array<keyof typeof TaskStatus>
      ).map((key) => ({
        value: TaskStatus[key],
        name: formatStatusLabel(key),
        color: STATUS_COLORS[TaskStatus[key]],
      }));
    } else if (item.status == "on_hold") {
      allStatus = (
        Object.keys({
          IN_PROGRESS: "in_progress",
          DONE: "done",
        }) as Array<keyof typeof TaskStatus>
      ).map((key) => ({
        value: TaskStatus[key],
        name: formatStatusLabel(key),
        color: STATUS_COLORS[TaskStatus[key]],
      }));
    } else if (item.status == "qa_failed") {
      if (
        usersStore.userProfile?.role == "admin" ||
        usersStore.userProfile?.role == "supervisor"
      ) {
        allStatus = (
          Object.keys({
            IN_PROGRESS: "in_progress",
            ON_HOLD: "on_hold",
            DONE: "done",
            CLOSED: "closed",
          }) as Array<keyof typeof TaskStatus>
        ).map((key) => ({
          value: TaskStatus[key],
          name: formatStatusLabel(key),
          color: STATUS_COLORS[TaskStatus[key]],
        }));
      } else {
        allStatus = (
          Object.keys({
            IN_PROGRESS: "in_progress",
            ON_HOLD: "on_hold",
            DONE: "done",
          }) as Array<keyof typeof TaskStatus>
        ).map((key) => ({
          value: TaskStatus[key],
          name: formatStatusLabel(key),
          color: STATUS_COLORS[TaskStatus[key]],
        }));
      }
    }
  }

  allStatus.push({
    value: "archive",
    name: "ARCHIVE",
    color: "",
  });

  return allStatus;
};

const selectStatus = async (item: ITaskData, status: IDropdown) => {
  if (item.status == "on_hold") {
    const data = await taskStore.resumeTask(tasksLoading, item.id);
    if (data) {
      if (status.value == "in_progress") {
        pushNotification("Successfully changed", "success");
        await getList();
      } else {
        const data = await taskStore.doneTask(tasksLoading, item.id);
        if (data) {
          pushNotification("Successfully changed", "success");
          await getList();
        }
      }
    }
  } else {
    if (status.value == "on_hold") {
      const data = await taskStore.holdTask(tasksLoading, item.id);
      if (data) {
        pushNotification("Successfully changed", "success");
        await getList();
      }
    } else if (status.value == "in_progress") {
      const data = await taskStore.startTask(tasksLoading, item.id);
      if (data) {
        pushNotification("Successfully changed", "success");
        await getList();
      }
    } else if (status.value == "done") {
      const data = await taskStore.doneTask(tasksLoading, item.id);
      if (data) {
        pushNotification("Successfully changed", "success");
        await getList();
      }
    } else if (status.value == "closed") {
      await taskStore.closeTask(tasksLoading, item.id, async (data: any) => {
        if (data) {
          pushNotification("Successfully changed", "success");
          await getList();
        }
      });
    } else if (status.value == "archive") {
      openArchiveModal(item);
    }
  }
};

const createNewTask = async () => {
  if (isSaving.value) return;
  if (
    !problems.value.length ||
    !newTask.value.company_id ||
    !newTask.value.driver_id ||
    !newTask.value.work_type_id
  ) {
    return;
  }

  isSaving.value = true;

  try {
    if (newTask.value.company_name) {
      newTask.value.company_id = "";
      delete newTask.value.company_id;
    } else {
      delete newTask.value.company_name;
    }

    if (newTask.value.driver_name) {
      newTask.value.driver_id = "";
      delete newTask.value.driver_id;
    } else {
      delete newTask.value.driver_name;
    }

    if (newTaskStatus.value == "new") {
      newTask.value.assignee_id = usersStore.userProfile?.id as string;
      await taskStore.createTask(tasksLoading, newTask.value, (data: any) => {
        if (data) {
          if (Object.keys(data).length) {
            pushNotification("Successfully added", "success");
            getList();
          }
        }
      });
    } else {
      let result: any = { ...newTask.value };
      result.description = description.value;
      delete result.assignee_id;
      await taskStore.createInformTask(tasksLoading, result, (data: any) => {
        if (data) {
          if (Object.keys(data).length) {
            pushNotification("Successfully added", "success");
            getList();
            newTaskStatus.value = "new";
            description.value = "";
          }
        }
      });
    }
  } finally {
    isSaving.value = false;
  }
};

const refreshTaskWorkTypes = async (taskId: string) => {
  try {
    const { data } = await taskService.getTaskById(taskId);

    const index = tasks.value.findIndex((task) => task.id === taskId);

    if (index === -1) return;

    const current = tasks.value[index];

    tasks.value[index] = {
      ...current,
      ...data,
      isEditingProblem: current.isEditingProblem,
      isEditingUser: current.isEditingUser,
    };
  } catch (err: any) {
    pushNotification(
      err.response?.data?.detail || "Failed to refresh task",
      "error"
    );
  }
};

const updateProblem = async (e: any, item: ITaskData) => {
  e.stopPropagation();
  if (!item.work_type_id) return;

  await taskStore.changeTaskWorkType(
    tasksLoading,
    item.id,
    { work_type_id: item.work_type_id },
    (data: any) => {
      if (data) {
        pushNotification("Successfully added", "success");
        getList();
      }
    }
  );
};

const formatToEdt = (date: any) => {
  if (!date) return "";

  return date.split("T")[1] == "11:00:00.000Z"
    ? DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d")
    : DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d, HH:mm ZZZZ");
};

const tooltipContent = (list: any[]) =>
  list
    ?.map((item) => `<p>${item.label} - ${formatToEdt(item.date)}</p>`)
    ?.join("");

const createItems = (type: string) => {
  let allStatus: any[];

  if (type == "new") {
    allStatus = (
      Object.keys({
        INFORM: "inform",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  } else {
    allStatus = (
      Object.keys({
        NEW: "new",
      }) as Array<keyof typeof TaskStatus>
    ).map((key) => ({
      value: TaskStatus[key],
      name: formatStatusLabel(key),
      color: STATUS_COLORS[TaskStatus[key]],
    }));
  }

  return allStatus;
};

const dropdownSelect = (e: any) => {
  if (e.value === "inform") {
    newModal.value = true;
    description.value = "";
  } else {
    newTaskStatus.value = "new";
  }
};

const saveModal = async () => {
  newTaskStatus.value = "inform";
  newModal.value = false;
  newTask.value.work_type_id = "26317c42-8a8e-4666-b78a-eca83fbf1701";
  await nextTick();
};

const editModal = () => {
  newModal.value = true;
};

function enterFirst() {
  const el = dropdown2.value?.[0];

  if (!el) return;
  el.focus();
}

const goLink = (e: any, item: any) => {
  e.stopPropagation();
  window.open(item.eld_link, "_blank")?.focus();
};

const enterSecond = async () => {
  const el = dropdown3.value?.[0];

  if (!el) return;
  el.focus();
};

const searchCompany = (val: string) => {
  companySearchVersion++;

  if (searchCompanyTimer) clearTimeout(searchCompanyTimer);

  searchCompanyTimer = setTimeout(() => {
    searchCompanyTimer = null;
    void fetchCompanyOptions(val);
  }, 300);
};

const searchDriver = (val: string) => {
  driverSearchVersion++;

  if (searchDriverTimer) clearTimeout(searchDriverTimer);

  searchDriverTimer = setTimeout(() => {
    searchDriverTimer = null;
    void fetchDriverOptions(val);
  }, 300);
};

const searchProblems = (val: string) => {
  problemSearchVersion++;

  if (searchProblemTimer) clearTimeout(searchProblemTimer);

  searchProblemTimer = setTimeout(() => {
    searchProblemTimer = null;
    void fetchProblemOptions(val);
  }, 300);
};

const searchFilterWorkType = (val: string) => {
  filterWorkTypeVersion++;

  if (searchFilterProblemTimer) {
    clearTimeout(searchFilterProblemTimer);
  }

  searchFilterProblemTimer = setTimeout(async () => {
    searchFilterProblemTimer = null;
    const version = ++filterWorkTypeVersion;

    try {
      const { data } = await workTypeService.getWorkTypes({
        is_active: true,
        search: val.trim(),
      });

      if (version !== filterWorkTypeVersion) return;

      workTypes.value = data;
    } catch (error) {
      if (version === filterWorkTypeVersion) {
        console.error("Failed to load work type filter", error);
      }
    }
  }, 300);
};

const searchFilterUser = (val: string) => {
  if (searchFilterUserTimer) clearTimeout(searchFilterUserTimer);
  searchFilterUserTimer = setTimeout(async () => {
    usersStore.commonFilter.q = val;
    const [fetchedUsers] = await Promise.all([
      usersStore.getUsers(userLoading),
    ]);

    users.value = fetchedUsers;
  });
};

const openArchiveModal = (item: ITaskData) => {
  archiveModal.value = true;
  archiveReason.value = "";
  archiveModalItem.value = item;
};

const closeArchiveModal = () => {
  archiveModal.value = false;
  archiveReason.value = "";
  archiveModalItem.value = null;
};

const saveArchive = async () => {
  if (!archiveReason.value) return;
  if (!archiveModalItem.value) return;

  await archiveStore.archiveTask(
    tasksLoading,
    archiveModalItem.value.id,
    archiveReason.value,
    async (data: any) => {
      if (data) {
        pushNotification("Successfully archived", "success");
        closeArchiveModal();
        await getList();
      }
    }
  );
};

const rowClick = async (item: any, index: number) => {
  if (usersStore.userProfile?.role === "super_admin") {
    await taskStore.getTaskTimeline(taskModalLoading, item.id, (res: any) => {
      if (res) {
        taskModal.value = true;
        let comments: any[] = [];
        taskModalItem.value = res.data;
        if (taskModalItem.value.items && taskModalItem.value.items.length) {
          comments = taskModalItem.value.items.filter(
            (item: any) => item.event_type === "comment"
          );
          taskModalItem.value.items = taskModalItem.value.items.filter(
            (item: any) => item.event_type !== "comment"
          );
        }
        taskModalItem.value.comments = comments;
        taskModalItem.value.rest_short_info = item.rest_short_info;
      }
    });
  }
};

const displayDropdownItem = (name: string) => {
  const labels: any = {
    new: "New",
    inform: "Inform",
    in_progress: "In Progress",
    on_hold: "On Hold",
    done: "Done",
    qa_failed: "Qa Failed",
    closed: "Closed",
  };

  return labels[name];
};

const openEditingProblem = (e: any, item: any, type: boolean) => {
  e.stopPropagation();
  item.isEditingProblem = type;
};

// Reassign user

const assignSuggestions = ref<any[]>([]);

const openEditingUser = (e: any, item: any, type: boolean) => {
  e.stopPropagation();

  if (type) {
    taskStore.getReassignSuggestions(tasksLoading, item.id, (res: any) => {
      if (res.data.candidates && res.data.candidates.length) {
        assignSuggestions.value = res.data.candidates;
      } else {
        pushNotification("No free specialists", "error");
      }
    });
  }
  item.isEditingUser = type;
};

const reassignUser = async (e: any, item: any) => {
  e.stopPropagation();
  if (!item.reassignedUser) return;

  await taskStore.reassignTask(
    tasksLoading,
    item.id,
    {
      assignee_id: item.reassignedUser,
    },
    (res: any) => {
      if (res) {
        pushNotification("Successfully changed", "success");
        getList();
      }
    }
  );
};

// Adding comment

const addTaskModalComment = async () => {
  if (!taskModalItem.value || !taskModalComment.value) return;

  await taskStore.addComment(
    taskModalLoading,
    taskModalItem.value.task_id,
    { text: taskModalComment.value },
    (data: any) => {
      if (data) {
        pushNotification("Successfully added", "success");
        closeTaskModal();
        getList();
      }
    }
  );
};

const closeTaskModal = () => {
  taskModalItem.value = undefined;
  taskModal.value = false;
  taskModalComment.value = "";
};

const copyRestInfo = async (item: string) => {
  await navigator.clipboard.writeText(item);
  pushNotification("Successfully copied", "success");
};

const copyAllInfo = async () => {
  let result = "";

  Object.keys(taskModalItem.value.rest_short_info.items).forEach(
    (item: any) => {
      if (
        taskModalItem.value.rest_short_info.items[item] &&
        taskModalItem.value.rest_short_info.items[item].copyContent
      ) {
        result += taskModalItem.value.rest_short_info.items[item].copyContent;
      }
    }
  );
  await navigator.clipboard.writeText(result);
  pushNotification("Successfully copied", "success");
};

const isChecking = ref<boolean>(false);
const uid = ref("");
const selectedRecords = ref<any[]>([]);

const selectedModel = computed({
  get() {
    return selectedRecords.value;
  },
  set(value) {
    selectedRecords.value = value;
  },
});

const isSelected = (item: any) => {
  return selectedModel.value.some((row: any) => row === item.id);
};

const goToCheck = () => {
  isChecking.value = true;
};

const saveCheck = async () => {
  if (!selectedModel.value.length) return;
  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");
  await checkStore.createManualSelection(
    tasksLoading,
    {
      organization_id: organizationId,
      task_ids: selectedModel.value,
    },
    (res: unknown) => {
      if (res) {
        pushNotification("Successfully created", "success");
        isChecking.value = false;
      }
    }
  );
};

onMounted(async () => {
  companyStore.commonSimpleFilter.search = "";
  companyStore.commonSimpleFilter.per_page = 50;
  driverStore.commonSimpleFilter.search = "";
  workTypeStore.commonFilter.search = "";

  if (usersStore.userProfile?.role === "admin") {
    isAdmin.value = true;
  }

  if (taskStore.commonFilter.search) {
    search.value = taskStore.commonFilter.search;
  }

  if (taskStore.commonFilter.team_id) {
    team.value = taskStore.commonFilter.team_id;
  } else {
    if (usersStore.userProfile && usersStore.userProfile.team_id) {
      team.value = usersStore.userProfile.team_id;
      companyStore.commonSimpleFilter.team_id = usersStore.userProfile.team_id;
      taskStore.commonFilter.team_id = usersStore.userProfile.team_id;
    }
  }

  if (taskStore.commonFilter.status) {
    status.value = taskStore.commonFilter.status;
  }

  if (taskStore.commonFilter.work_type_id) {
    problem.value = taskStore.commonFilter.work_type_id;
  }
  await getList();

  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    },
    { root: scrollContainer.value, rootMargin: "200px" }
  );
  if (sentinel.value) observer.observe(sentinel.value);

  await nextTick();
  dropdown1.value?.[0]?.focus();
});

onBeforeMount(() => {
  uid.value = crypto.randomUUID();
});

watch(search, (val) => {
  taskStore.commonFilter.search = val;

  // Invalidate all previous requests immediately.
  filterVersion++;

  if (searchTimer) clearTimeout(searchTimer);

  searchTimer = setTimeout(() => {
    searchTimer = null;
    void resetAndFetch();
  }, 300);
});

watch(team, (val: string) => {
  taskStore.commonFilter.team_id = val;
  void resetAndFetch();
});

watch(user, (val: string) => {
  taskStore.commonFilter.assignee_id = val;
  void resetAndFetch();
});

watch(status, (val: string) => {
  taskStore.commonFilter.status = val;
  void resetAndFetch();
});

watch(problem, (val: string) => {
  taskStore.commonFilter.work_type_id = val;
  void resetAndFetch();
});

watch(
  () => newTask.value.company_id,
  async (val) => {
    driverSearchVersion++;

    if (searchDriverTimer) {
      clearTimeout(searchDriverTimer);
      searchDriverTimer = null;
    }

    driverStore.commonSimpleFilter.search = "";

    const newVal = companies.value.some(
      (item) => item.id.toLowerCase() === val
    );
    drivers.value = [];
    newTask.value.driver_id = "";
    newTask.value.work_type_id = "";

    if (!newVal) {
      if (val) {
        newTask.value.company_name = val;
      } else {
        delete newTask.value.company_name;
      }

      driverStore.commonSimpleFilter.company_id = "";
      drivers.value = [];
    } else {
      delete newTask.value.company_name;

      if (val) {
        driverStore.commonSimpleFilter.company_id = val;
        await fetchDriverOptions("", val);
      }
    }
  }
);

watch(
  () => newTask.value.driver_id,
  (val) => {
    const newVal = drivers.value.some((item) => item.id.toLowerCase() === val);

    if (!newVal) {
      newTask.value.driver_name = val;
    }
  }
);

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);

onUnmounted(() => {
  if (searchTimer) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }
  filterVersion++;
  companySearchVersion++;
  driverSearchVersion++;
  problemSearchVersion++;
  filterWorkTypeVersion++;
  observer?.disconnect();
  taskStore.clearCommonFilter();
  companyStore.clearSimpleFilter();
  driverStore.clearCommonSimpleFilter();
  workTypeStore.clearFilter();
  usersStore.clearFilter();
});
</script>

<template>
  <div class="v-tasks">
    <div class="v-tasks__header">
      <CommonSearch
        v-model="search"
        :icon="SearchMd"
        :placeholder="'Search'"
        clearable
      />
      <CommonDropdown
        v-model="team"
        :items="teams.map((t: any) => ({ value: t.id, name: t.name }))"
        label="Team"
        position="bottom"
        dropdownFullWidth
        clearable
      />

      <CustomSearchDropdown
        v-model="user"
        :items="userFilterItems"
        label="User"
        position="bottom"
        clearable
        dropdownFullWidth
        maxWidth="200px"
        @search="searchFilterUser"
      />

      <CommonDropdown
        v-model="status"
        :items="statusItems"
        label="Status"
        position="bottom"
        clearable
      />

      <CustomSearchDropdown
        v-model="problem"
        :items="problemFilterItems"
        label="Problem"
        position="bottom"
        clearable
        dropdownFullWidth
        maxWidth="400px"
        @search="searchFilterWorkType"
      />

      <template
        v-if="
          usersStore.userProfile?.role == 'super_admin' ||
          usersStore.userProfile?.role == 'admin' ||
          usersStore.userProfile?.role == 'supervisor'
        "
      >
        <CommonButton
          v-if="isChecking"
          type="primary"
          :label="`Go to check (${selectedModel.length})`"
          :loading="tasksLoading"
          :isDisabled="tasksLoading"
          @clicked="saveCheck"
        />
        <CommonButton
          v-else
          style="border-color: var(--color-primary)"
          type="outline-primary"
          label="Check"
          @clicked="goToCheck"
        />
      </template>
    </div>

    <div class="v-tasks__body">
      <template
        v-for="(dateGroup, dateIndex) in groupedByDate"
        :key="dateIndex"
      >
        <div
          class="v-tasks__date-row"
          @click="toggleDate(dateGroup.date)"
          :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
        >
          <span class="v-tasks__date-label">{{ dateGroup.date }}</span>
          <span
            class="v-tasks__date-chevron"
            :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
          >
            <ChevronDown :size="20" />
          </span>
        </div>

        <template v-if="!isDateCollapsed(dateGroup.date)">
          <template
            v-for="(shiftGroup, shiftIndex) in dateGroup.shiftGroups"
            :key="shiftIndex"
          >
            <CommonTable
              :headers="filteredItems"
              :groups="[{ label: shiftGroup.label, items: shiftGroup.items }]"
              :loading="tasksLoading"
              :collapsed="isShiftCollapsed(dateGroup.date, shiftGroup.label)"
              @toggle-group="toggleShift(dateGroup.date, shiftGroup.label)"
              isTaskTable
              :dateIndex="dateIndex"
              :shiftIndex="shiftIndex"
              :isAdmin="isAdmin"
              :withoutHeader="shiftIndex > 0"
              @row-click="rowClick"
            >
              <template #id="{ item, index }">
                <label
                  v-if="isChecking"
                  class="custom__checkbox"
                  :class="isSelected(item) ? 'active' : ''"
                  @click.stop
                >
                  <input
                    @click.stop
                    v-model="selectedModel"
                    :value="item.id"
                    class="ui-checkbox form-checkbox"
                    type="checkbox"
                    :name="uid"
                  />

                  <Check :size="14" />
                </label>

                <span v-else>{{ index + 1 }}</span>
              </template>

              <template #company="{ item }">
                <div class="company__name">
                  <span class="title">
                    {{ item.company_name }}
                    <span
                      class="note__icon"
                      v-if="item.company_note"
                      v-tooltip="{
                        content: item.company_note,
                        html: true,
                      }"
                    >
                      <InfoCircle :size="14" />
                    </span>
                  </span>
                  <span class="app">{{
                    item.provider_name?.split(" ")[0] || "Hos"
                  }}</span>
                </div>
              </template>

              <template #drivers="{ item }">
                <span class="v-users__badge driver__name">
                  {{ item.driver_name }}
                  <span
                    class="note__icon"
                    v-if="item.driver_note"
                    v-tooltip="{
                      content: item.driver_note,
                      html: true,
                    }"
                  >
                    <InfoCircle :size="14" />
                  </span>
                </span>
              </template>

              <template #team="{ item }">
                <span class="v-users__badge">{{ item.team_name }}</span>
              </template>

              <template #users="{ item }">
                <div class="custom__user--info" v-if="!item.isEditingUser">
                  <span class="title">{{ item.assignee_name }}</span>

                  <div
                    class="edit"
                    @click="(e: any) => openEditingUser(e, item, true)"
                    v-if="
                      (usersStore.userProfile?.role === 'super_admin' ||
                        usersStore.userProfile?.role === 'admin' ||
                        usersStore.userProfile?.role === 'supervisor') &&
                      item.status == 'in_progress'
                    "
                  >
                    <Pencil02 :size="16" />
                  </div>
                </div>

                <div v-else class="custom__problem">
                  <CustomTableDropdown
                    v-model="item.reassignedUser"
                    :items="
                                      assignSuggestions.map((p: any) => ({
                                        value: p.id,
                                        name: p.name,
                                      }))
                                    "
                    label="User"
                    position="bottom"
                    maxWidth="100%"
                    dropdownFullWidth
                  />

                  <div
                    class="clear"
                    @click="(e: any) => openEditingUser(e, item, false)"
                  >
                    <X :size="16" />
                  </div>

                  <div
                    class="problem__save"
                    :class="!item.work_type_id ? 'disabled' : ''"
                    @click="(e: any) => reassignUser(e, item)"
                  >
                    Save
                  </div>
                </div>
              </template>

              <template #date="{ item }">
                <span>{{ datepickerToUtcDateOnly(item.created_time) }}</span>
              </template>

              <template #completed_time="{ item }">
                <span>{{
                  datepickerToUtcDateOnly(item.completed_time) || "-"
                }}</span>
              </template>

              <template #status="{ item }">
                <CommonStatus
                  v-if="item.status && getStatus(item.status)"
                  :status="getStatus(item.status)!"
                  @next="nextStatus(item)"
                  @confirm="confirmStatus(item)"
                  @selected="(e: any) => selectStatus(item, e)"
                  :showActions="hasUserHasAction(item)"
                  :items="availableStatusItems(item)"
                  :tooltipContent="
                    tooltipContent(item.qa_failed_reason_details)
                  "
                />
                <span v-else>—</span>
              </template>

              <template #problems="{ item }">
                <div
                  class="custom__problem--info"
                  v-if="!item.isEditingProblem"
                >
                  <span class="title">{{
                    item.description || item.work_type_name
                  }}</span>
                  <div
                    class="edit"
                    @click="(e: any) => openEditingProblem(e, item, true)"
                    v-if="
                      hasUserHasAction(item) &&
                      item.status == 'in_progress' &&
                      item.source !== 'eld_import'
                    "
                  >
                    <Pencil02 :size="16" />
                  </div>
                </div>

                <div v-else class="custom__problem">
                  <CustomTableDropdown
                    v-model="item.work_type_id"
                    :items="
                                      problems.map((p: any) => ({
                                        value: p.id,
                                        name: p.name,
                                      }))
                                    "
                    label="Problem"
                    position="bottom"
                    maxWidth="100%"
                    dropdownFullWidth
                    @search="searchProblems"
                  />

                  <div
                    class="clear"
                    @click="(e: any) => openEditingProblem(e, item, false)"
                  >
                    <X :size="16" />
                  </div>

                  <div
                    class="problem__save"
                    :class="!item.work_type_id ? 'disabled' : ''"
                    @click="(e: any) => updateProblem(e, item)"
                  >
                    Save
                  </div>
                </div>

                <TaskAdditionalWorkTypes
                  :task="item"
                  :work-types="workTypes"
                  :can-edit="Boolean(hasUserHasAction(item))"
                  @updated="refreshTaskWorkTypes(item.id)"
                />
              </template>

              <template #actions="{ item }">
                <div v-if="item.eld_link" class="icon">
                  <LinkExternal02
                    :size="16"
                    @click="(e: any) => goLink(e, item)"
                  />
                </div>
              </template>

              <template v-if="!isChecking" #new-row>
                <td></td>
                <td class="td__dropdown">
                  <CustomTableDropdown
                    ref="dropdown1"
                    v-model="newTask.company_id"
                    :items="companyItems"
                    label="Company"
                    position="bottom"
                    clearable
                    maxWidth="100%"
                    dropdownFullWidth
                    isAppendItem
                    @enter="enterFirst"
                    creatable
                    @search="searchCompany"
                  />
                </td>

                <td class="td__dropdown">
                  <CustomTableDropdown
                    ref="dropdown2"
                    v-model="newTask.driver_id"
                    :items="driverItems"
                    label="Driver"
                    position="bottom"
                    clearable
                    dropdownFullWidth
                    maxWidth="100%"
                    isAppendItem
                    @enter="enterSecond"
                    creatable
                    @search="searchDriver"
                  />
                </td>
                <td v-if="!taskStore.commonFilter.team_id">
                  {{ usersStore.userProfile?.team_name }}
                </td>
                <td>{{ usersStore.userProfile?.name }}</td>
                <td>{{ datepickerToUtcDateOnly(new Date()) }}</td>
                <td>-</td>
                <td>-</td>
                <td>
                  <CommonStatus
                    :status="getStatus(newTaskStatus)"
                    @next="console.log('Next driver-status')"
                    @confirm="console.log('Confirm driver-status')"
                    :items="createItems(newTaskStatus)"
                    @selected="dropdownSelect"
                    :arrow="false"
                    showActions
                  />
                </td>
                <td class="td__dropdown">
                  <div class="problem__group">
                    <CustomTableDropdown
                      ref="dropdown3"
                      v-model="newTask.work_type_id"
                      :items="problemItems"
                      label="Problem"
                      position="bottom"
                      clearable
                      dropdownFullWidth
                      isAppendItem
                      @enter="createNewTask"
                      @search="searchProblems"
                    />

                    <div
                      class="problem__group--edit"
                      v-if="newTaskStatus == 'inform'"
                      @click="editModal"
                    >
                      <Pencil02 :size="16" />
                    </div>

                    <div
                      class="problem__group--save"
                      :class="
                        isSaving ||
                        !problems.length ||
                        !newTask.company_id ||
                        !newTask.driver_id ||
                        !newTask.work_type_id
                          ? 'disabled'
                          : ''
                      "
                      @click="!isSaving && createNewTask()"
                    >
                      Save
                    </div>
                  </div>
                </td>
              </template>
            </CommonTable>
          </template>
        </template>
      </template>

      <div ref="sentinel" class="v-tasks__sentinel"></div>

      <div v-if="isLoadingMore" class="v-tasks__loading-more">
        Loading more…
      </div>
      <div
        v-else-if="!hasMore && tasks.length && !tasksLoading"
        class="v-tasks__end"
      >
        No more tasks
      </div>
    </div>

    <CommonModal v-model="newModal" title="Description">
      <div class="new-modal">
        <CommonSearch
          v-model="description"
          placeholder="Type here ..."
          width="100%"
        />
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton
            type="outline"
            label="Cancel"
            @clicked="newModal = false"
          />
          <CommonButton type="primary" label="Save" @clicked="saveModal" />
        </div>
      </template>
    </CommonModal>

    <CommonModal v-model="archiveModal" title="Archive reason">
      <div class="new-modal">
        <CommonInput v-model="archiveReason" label="Reason" multiline />
      </div>

      <div class="auto-complete">
        <div class="item" @click="archiveReason = 'Duplicate task'">
          Duplicate task
        </div>
        <div class="item" @click="archiveReason = 'Wrong name'">Wrong name</div>
        <div class="item" @click="archiveReason = 'Need to change status'">
          Need to change status
        </div>
        <div class="item" @click="archiveReason = 'Another worker doing'">
          Another worker doing
        </div>
        <div class="item" @click="archiveReason = 'Created by mistake'">
          Created by mistake
        </div>
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton
            type="outline"
            label="Cancel"
            @clicked="closeArchiveModal"
          />
          <CommonButton type="primary" label="Save" @clicked="saveArchive" />
        </div>
      </template>
    </CommonModal>

    <!--  Task Modal  -->
    <CommonModal width="1200px" v-model="taskModal" title="Task details">
      <div class="task-modal">
        <div class="history">
          <div class="headline">History</div>
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Changed by</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in taskModalItem.items" :key="index">
                <td>
                  <template v-if="item.event_type == 'reassigned'">
                    <div class="change">
                      <span class="badge">{{
                        item.old_value?.assignee_name
                          ? item.old_value?.assignee_name
                          : "-"
                      }}</span>
                      <span class="icon">
                        <ArrowNarrowRight :size="20" />
                      </span>
                      <span class="badge">{{
                        item.new_value?.assignee_name
                          ? item.new_value.assignee_name
                          : "-"
                      }}</span>
                    </div>
                  </template>
                  <template v-else>
                    {{ item.changed_by_name }}
                  </template>
                </td>
                <td>
                  <div class="change">
                    <template
                      v-if="item.old_value?.status !== item.new_value?.status"
                    >
                      <span class="badge" :class="item.old_value?.status">{{
                        item.old_value?.status
                          ? displayDropdownItem(item.old_value?.status)
                          : "-"
                      }}</span>
                      <span class="icon">
                        <ArrowNarrowRight :size="20" />
                      </span>
                      <span class="badge" :class="item.new_value?.status">{{
                        item.new_value?.status
                          ? displayDropdownItem(item.new_value.status)
                          : "-"
                      }}</span>
                    </template>
                    <template v-else>
                      <span class="badge" :class="item.new_value?.status">{{
                        item.new_value?.status
                          ? displayDropdownItem(item.new_value.status)
                          : "-"
                      }}</span>
                    </template>
                  </div>
                </td>
                <td>{{ item.changed_by_name }}</td>
                <td>
                  {{ datepickerToUtcDateOnlyWithoutTime(item.created_time) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="comment">
          <div class="headline">Comment</div>
          <div class="body">
            <div class="body__list">
              <div
                v-if="taskModalItem.comments && taskModalItem.comments.length"
              >
                <div
                  class="item"
                  v-for="(item, index) in taskModalItem.comments"
                  :key="index"
                >
                  <div class="item__time">
                    {{ datepickerToUtcDateOnlyWithoutTime(item.created_time) }}
                  </div>
                  <div class="item__body">
                    <div class="item__body--user">
                      {{ item.changed_by_name }}
                    </div>
                    <div class="item__body--text">{{ item.comment }}</div>
                  </div>
                </div>
              </div>
              <div v-else>No comments</div>
            </div>
            <div class="body__footer">
              <input
                v-model="taskModalComment"
                type="text"
                placeholder="Write a comment ..."
              />

              <div class="icon" @click="addTaskModalComment">
                <Send03 :size="16" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="rest-info"
        v-if="
          taskModalItem.rest_short_info &&
          Object.keys(taskModalItem.rest_short_info.items).length
        "
      >
        <div class="headline">
          <div class="caption">Rest info</div>
          <div class="icon" @click="copyAllInfo">
            <Copy06 :size="16" />
          </div>
        </div>
        <div class="body">
          <template
            v-for="(item, index) in Object.keys(
              taskModalItem.rest_short_info.items
            )"
            :key="index"
          >
            <div v-if="taskModalItem.rest_short_info.items[item]" class="rest">
              <div class="rest__headline">
                <div class="caption">
                  {{ taskModalItem.rest_short_info.items[item].title }}
                </div>
                <div
                  v-if="taskModalItem.rest_short_info.items[item].copyContent"
                  class="icon"
                  @click="
                    copyRestInfo(
                      taskModalItem.rest_short_info.items[item].copyContent
                    )
                  "
                >
                  <Copy06 :size="16" />
                </div>
              </div>
              <div class="rest__time">
                <div class="duration">
                  <div class="title">Duration:</div>
                  <div class="value">
                    {{
                      taskModalItem.rest_short_info.items[item]
                        .durationSeconds || "-"
                    }}
                  </div>
                </div>
                <div class="from">
                  <div class="title">From:</div>
                  <div class="value">
                    {{
                      taskModalItem.rest_short_info.items[item].time?.utc
                        .displayFrom
                        ? taskModalItem.rest_short_info.items[
                            item
                          ].time?.utc.displayFrom.split(" UTC")[0]
                        : "-"
                    }}
                  </div>
                </div>
                <div class="to">
                  <div class="title">To:</div>
                  <div class="value">
                    {{
                      taskModalItem.rest_short_info.items[item].time?.utc
                        .displayTo
                        ? taskModalItem.rest_short_info.items[
                            item
                          ].time?.utc.displayTo.split(" UTC")[0]
                        : "-"
                    }}
                  </div>
                </div>
              </div>

              <div class="rest__location">
                <div class="location">
                  <div class="title">Location:</div>
                  <div class="value">
                    {{
                      taskModalItem.rest_short_info.items[item].location?.raw ||
                      "-"
                    }}
                  </div>
                </div>

                <div class="status">
                  <div class="title">Status</div>
                  <div class="value">
                    <CustomStatusBadge
                      v-if="taskModalItem.rest_short_info.items[item].status"
                      :label="taskModalItem.rest_short_info.items[item].status"
                    />
                    <span v-else>-</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-tasks {
  padding: var(--base-space-16);
  background: var(--color-system-background);
  height: calc(100vh - 80px);
  overflow: auto;

  &__header {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: var(--base-space-8);
    margin-bottom: var(--base-space-16);
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--base-space-4) var(--base-space-8);
    gap: var(--base-space-8);
    border-radius: var(--base-radius-8);
    color: var(--color-label);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;

    &.active {
      background-color: var(--color-green-light);
    }

    &.inactive {
      background-color: var(--color-red-light);
    }
  }

  &__actions {
    display: flex;
    gap: var(--base-space-8);
  }

  &__btn {
    cursor: pointer;
    transition: opacity 0.2s;
    background-color: var(--color-bg-secondary);
    color: var(--color-label);
    border-color: var(--color-border);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--base-space-8);
    width: 32px;
    height: 32px;
    padding: 6px;
    border-radius: var(--base-radius-12);

    &:hover {
      opacity: 0.8;
    }
  }

  &__date-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--base-space-8);
    padding: 10px 16px;
    cursor: pointer;
    user-select: none;
    border-top-left-radius: var(--base-radius-16);
    border-top-right-radius: var(--base-radius-16);
    transition: background-color 0.15s;
    background: var(--color-secondary-system-background);
    margin-top: 10px;
    height: 40px;

    &.is-collapsed {
      border-radius: var(--base-radius-16);
    }
  }

  &__date-chevron {
    display: flex;
    align-items: center;
    color: var(--color-label-secondary);
    transition: transform 0.2s;

    &.is-collapsed {
      transform: rotate(-180deg);
    }
  }

  &__date-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-label);
  }

  &__sentinel {
    height: 1px;
  }

  &__loading-more,
  &__end {
    text-align: center;
    padding: var(--base-space-16);
    font-size: 13px;
    color: var(--color-label-secondary);
  }

  :deep(.c-co-table) {
    margin-top: 0 !important;
  }

  :deep(.table thead th) {
    &:first-child {
      border-top-left-radius: 0 !important;
    }

    &:last-child {
      border-top-right-radius: 0 !important;
    }
  }

  .td__dropdown {
    padding: 0;

    .problem__group {
      display: flex;

      &--save,
      &--edit {
        margin: 8px 12px;
        background: var(--color-secondary-system-background);
        border-radius: var(--base-radius-8);
        padding: 4px 8px;
        color: var(--color-primary);
        font-size: 14px;
        line-height: 20px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;

        &.disabled {
          cursor: not-allowed;
          pointer-events: none;
        }
      }

      &--edit {
        margin: 8px 0 8px 12px;
      }
    }
  }

  .custom__problem {
    display: flex;
    align-items: center;

    .problem__save {
      background: var(--color-secondary-system-background);
      border-radius: var(--base-radius-8);
      padding: 4px 8px;
      color: var(--color-primary);
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;

      &.disabled {
        cursor: not-allowed;
        pointer-events: none;
      }
    }

    .clear {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      margin-right: 4px;
    }
  }

  .custom__problem--info,
  .custom__user--info {
    display: flex;
    align-items: center;

    .title {
      flex: 1;
    }

    .edit {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: var(--color-secondary-system-background);
      border-radius: var(--base-radius-8);
      padding: 4px 8px;
      color: var(--color-primary);
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
    }
  }
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}

.company__name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;

  .title {
    display: flex;
    align-items: center;

    .note__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      margin-left: 8px;
      color: var(--color-primary);
    }
  }

  .app {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: var(--color-label-secondary);
    white-space: nowrap;
  }
}

.driver__name {
  display: flex;
  align-items: center;

  .note__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    margin-left: 8px;
    color: var(--color-primary);
  }
}

.auto-complete {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: var(--base-space-8);
  flex-wrap: wrap;

  .item {
    cursor: pointer;
    border: 1px solid var(--color-border);
    color: var(--color-label);
    padding: 10px 14px;
    border-radius: 14px;
    background: var(--color-secondary-system-background);
  }
}

.task-modal {
  display: flex;
  gap: var(--base-space-12);
  color: var(--color-label);

  .history {
    flex: 2;
    border: 1px solid var(--color-border);
    border-radius: 14px;
    padding: 12px;
    background: var(--color-secondary-system-background);

    table {
      background: var(--color-system-background);
      width: 100%;
      border-collapse: separate;
      border: 1px solid var(--color-border);
      border-radius: 14px;
      border-spacing: 0;

      th,
      td {
        border-right: 1px solid var(--color-border);
        border-bottom: 1px solid var(--color-border);
        padding: 12px 16px;
      }

      th:last-child,
      td:last-child {
        border-right: none;
      }

      tr:last-child td {
        border-bottom: none;
      }

      thead {
        th {
          font-size: 14px;
          line-height: 20px;
          font-weight: 600;
          text-align: left;
          color: var(--color-label-secondary);
        }
      }

      tbody {
        tr {
          cursor: pointer;

          &.active {
            background: var(--color-secondary-system-background);
          }

          td {
            font-weight: 500;
            font-size: 14px;
            line-height: 20px;
          }
        }
      }

      .change {
        display: flex;
        align-items: center;
        gap: 10px;

        .badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          background: var(--color-grey-light);

          &.new {
            background: var(--color-blue-light);
          }

          &.on_hold {
            background: var(--color-yellow-light);
          }

          &.in_progress {
            background: var(--color-orange-light);
          }

          &.closed {
            background: var(--color-green-light);
          }

          &.inform {
            background: var(--color-grey-light);
          }

          &.qa_failed {
            background: var(--color-red-light);
          }

          &.done {
            background: var(--color-green-light);
          }
        }

        .icon {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .comment {
    flex: 1;
    border: 1px solid var(--color-border);
    border-radius: 14px;
    padding: 12px;
    background: var(--color-secondary-system-background);
    display: flex;
    flex-direction: column;

    .body {
      display: flex;
      flex-direction: column;
      width: 100%;
      border-radius: 14px;
      background: var(--color-system-background);
      justify-content: space-between;
      flex: 1;
      padding: 12px;
      border: 1px solid var(--color-border);

      &__list {
        flex: 1;

        .item {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          margin-bottom: 4px;

          &__time {
            font-size: 10px;
            line-height: 14px;
            width: 30px;
            min-width: 30px;
          }

          &__body {
            background: var(--color-secondary-system-background);
            border-radius: 8px;
            padding: 8px 12px;

            &--user {
              font-size: 12px;
              color: var(--color-primary);
              margin-bottom: 4px;
            }

            &--text {
              font-size: 14px;
            }
          }
        }
      }

      &__footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-radius: 8px;
        border: 1px solid var(--color-border);
        padding: 12px;

        input {
          color: var(--color-label);
          border: none;

          &:focus {
            outline: none;
          }
        }

        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          cursor: pointer;
          border-radius: 50%;

          &:hover {
            background: var(--color-secondary-system-background);
          }
        }
      }
    }
  }

  .headline {
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    margin-bottom: 8px;
  }
}

.rest-info {
  border: 1px solid var(--color-border);
  border-radius: 14px;
  background: var(--color-secondary-system-background);
  padding: 12px;
  margin-top: 16px;
  color: var(--color-label);

  .headline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .caption {
      font-size: 20px;
      line-height: 28px;
      font-weight: 600;
    }

    .icon {
      width: 28px;
      height: 28px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 8px;
      color: var(--color-primary);
      background: var(--color-system-background);
      cursor: pointer;
    }
  }

  .body {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;

    .rest {
      background: var(--color-system-background);
      border: 1px solid var(--color-border);
      border-radius: 18px;
      padding: 16px;

      &__headline {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .caption {
          font-size: 24px;
          line-height: 32px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .icon {
          width: 28px;
          height: 28px;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 8px;
          color: var(--color-primary);
          background: var(--color-secondary-system-background);
          cursor: pointer;
        }
      }

      .title {
        font-size: 14px;
        line-height: 20px;
        font-weight: 500;
        color: var(--color-label-secondary);
      }

      .value {
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
      }

      &__time {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;

        .duration,
        .from,
        .to {
          border: 1px solid var(--color-border);
          border-radius: 12px;
          padding: 8px 12px;
        }
      }

      &__location {
        display: grid;
        grid-template-columns: 2fr 1fr;
        gap: 8px;
        margin-top: 8px;

        .location,
        .status {
          border: 1px solid var(--color-border);
          border-radius: 12px;
          padding: 8px 12px;
        }
      }
    }
  }
}

.custom__checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 20px;
  height: 20px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  color: transparent;

  &.active {
    background: var(--color-primary);
    color: var(--color-white);
    border-color: var(--color-primary);
  }

  input {
    display: none;
  }
}
</style>
